import React from 'react';

import { cn } from '@/lib/utils';
import type { DocumentInstanceRead } from '@/services/api/generated/hipaaESignService';

type ESignStatus = DocumentInstanceRead['status'];

interface HipaaESignStatusBadgeProps {
  status: ESignStatus;
  size?: 'sm' | 'md';
  showPulse?: boolean;
}

const getStatusConfig = (
  status: ESignStatus,
): { label: string; className: string; shouldPulse: boolean } => {
  switch (status) {
    case 'CREATED':
      return {
        label: 'Request Created',
        className: 'bg-gray-100 text-gray-700 border-gray-200',
        shouldPulse: false,
      };
    case 'SENT':
      return {
        label: 'Awaiting Signature',
        className: 'bg-amber-100 text-amber-800 border-amber-200',
        shouldPulse: true,
      };
    case 'SIGNED':
    case 'COMPLETED':
      return {
        label: 'Signed',
        className: 'bg-green-100 text-green-800 border-green-200',
        shouldPulse: false,
      };
    case 'VOIDED':
      return {
        label: 'Cancelled',
        className: 'bg-red-100 text-red-700 border-red-200',
        shouldPulse: false,
      };
    default:
      return {
        label: status,
        className: 'bg-gray-100 text-gray-700 border-gray-200',
        shouldPulse: false,
      };
  }
};

export const HipaaESignStatusBadge: React.FC<HipaaESignStatusBadgeProps> = ({
  status,
  size = 'md',
  showPulse = true,
}) => {
  const config = getStatusConfig(status);
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        config.className,
        sizeClasses[size],
        showPulse && config.shouldPulse && 'animate-pulse',
      )}
    >
      {config.shouldPulse && (
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
        </span>
      )}
      {config.label}
    </span>
  );
};
